import React from 'react';
import { Box, Container, Typography, Button } from '@mui/material';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Link as ScrollLink } from 'react-scroll';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HandshakeRoundedIcon from '@mui/icons-material/HandshakeRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useT } from '../i18n/useT';

const PricingSection: React.FC = () => {
  const { t, r } = useT();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const location = useLocation();

  const included = r.pricing.included;
  const isHome = location.pathname === '/';
  const contactHref = `mailto:info@waiworking.com?subject=${encodeURIComponent(t('pricing.contactSubject'))}`;

  return (
    <Box
      component="section"
      id="pricing"
      aria-labelledby="pricing-heading"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: '#F8FAFC',
        borderTop: '1px solid #E2E8F0',
        borderBottom: '1px solid #E2E8F0',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6 }}
        >
          {/* Hero block */}
          <Box sx={{ textAlign: 'center', maxWidth: 820, mx: 'auto', mb: { xs: 5, md: 7 } }}>
            <Typography
              component="span"
              sx={{
                display: 'inline-block',
                px: 1.4,
                py: 0.5,
                borderRadius: 999,
                backgroundColor: 'white',
                border: '1px solid #E2E8F0',
                color: '#64748B',
                fontWeight: 700,
                fontSize: '0.72rem',
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                mb: 2.5,
              }}
            >
              {t('pricing.eyebrow')}
            </Typography>
            <Typography
              component="h2"
              id="pricing-heading"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '2rem', md: '2.6rem' },
                lineHeight: 1.15,
                letterSpacing: '-0.02em',
                background: 'linear-gradient(45deg, #00F5FF, #FF00FF)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              {t('pricing.title')}
            </Typography>
            <Typography
              sx={{
                color: '#475569',
                lineHeight: 1.65,
                fontSize: { xs: '1rem', md: '1.1rem' },
              }}
            >
              {t('pricing.subtitle')}
            </Typography>
          </Box>

          {/* 2-column body card */}
          <Box
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              border: '1px solid #E2E8F0',
              backgroundColor: 'white',
              boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.15fr 1fr' },
            }}
          >
            {/* LEFT — what's included */}
            <Box sx={{ p: { xs: 3.5, md: 5 } }}>
              <Typography
                component="h3"
                sx={{
                  fontWeight: 700,
                  fontSize: { xs: '1.05rem', md: '1.15rem' },
                  color: '#0F172A',
                  letterSpacing: '-0.01em',
                  mb: 2.5,
                }}
              >
                {t('pricing.includedTitle')}
              </Typography>
              <Box
                component="ul"
                sx={{
                  listStyle: 'none',
                  p: 0,
                  m: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                {included.map((item) => (
                  <Box
                    component="li"
                    key={item}
                    sx={{ display: 'flex', gap: 1.2, alignItems: 'flex-start' }}
                  >
                    <CheckCircleRoundedIcon sx={{ color: '#0F172A', fontSize: '1.25rem', mt: 0.1, flexShrink: 0 }} />
                    <Typography sx={{ color: '#334155', fontSize: '0.97rem', lineHeight: 1.55 }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  mt: 3.5,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: '#F8FAFC',
                  border: '1px solid #E2E8F0',
                  borderLeft: '3px solid #0F172A',
                }}
              >
                <Typography
                  sx={{
                    color: '#475569',
                    fontSize: '0.88rem',
                    lineHeight: 1.55,
                    fontStyle: 'italic',
                  }}
                >
                  {t('pricing.note')}
                </Typography>
              </Box>
            </Box>

            {/* RIGHT — partner CTA card */}
            <Box
              sx={{
                p: { xs: 3.5, md: 5 },
                backgroundColor: 'rgb(13, 13, 37)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  background:
                    'radial-gradient(circle at 80% 20%, rgba(0, 245, 255, 0.10) 0%, transparent 55%), radial-gradient(circle at 15% 90%, rgba(255, 0, 255, 0.08) 0%, transparent 55%)',
                  pointerEvents: 'none',
                },
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 1.6,
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.18)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2.5,
                  }}
                  aria-hidden
                >
                  <HandshakeRoundedIcon sx={{ color: 'white', fontSize: '1.8rem' }} />
                </Box>

                <Typography
                  component="h3"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.3rem', md: '1.55rem' },
                    color: 'white',
                    lineHeight: 1.2,
                    letterSpacing: '-0.015em',
                    mb: 1.5,
                  }}
                >
                  {t('pricing.cardTitle')}
                </Typography>
                <Typography
                  sx={{
                    color: 'rgba(255, 255, 255, 0.78)',
                    lineHeight: 1.55,
                    fontSize: { xs: '0.95rem', md: '1rem' },
                    mb: 3,
                  }}
                >
                  {t('pricing.cardSubtitle')}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, mt: 'auto' }}>
                  {isHome ? (
                    <ScrollLink to="partners" smooth duration={500} offset={-70} style={{ cursor: 'pointer' }}>
                      <Button
                        fullWidth
                        endIcon={<ArrowForwardRoundedIcon />}
                        sx={{
                          py: 1.3,
                          borderRadius: '50px',
                          backgroundColor: 'white',
                          color: '#0F172A',
                          fontWeight: 700,
                          fontSize: '0.95rem',
                          textTransform: 'none',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.92)',
                            transform: 'translateY(-2px)',
                          },
                        }}
                      >
                        {t('pricing.primaryCta')}
                      </Button>
                    </ScrollLink>
                  ) : (
                    <Button
                      component={RouterLink}
                      to="/partners"
                      fullWidth
                      endIcon={<ArrowForwardRoundedIcon />}
                      sx={{
                        py: 1.3,
                        borderRadius: '50px',
                        backgroundColor: 'white',
                        color: '#0F172A',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        textTransform: 'none',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.92)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      {t('pricing.primaryCta')}
                    </Button>
                  )}

                  <Button
                    component="a"
                    href={contactHref}
                    fullWidth
                    startIcon={<EmailRoundedIcon />}
                    sx={{
                      py: 1.2,
                      borderRadius: '50px',
                      backgroundColor: 'transparent',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.92rem',
                      textTransform: 'none',
                      border: '1px solid rgba(255, 255, 255, 0.35)',
                      '&:hover': {
                        borderColor: 'white',
                        backgroundColor: 'rgba(255, 255, 255, 0.06)',
                      },
                    }}
                  >
                    {t('pricing.secondaryCta')}
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

export default PricingSection;
